import * as React from "react";
import classnames from "classnames";

import { DataGrid, Getter, Setter } from '../../../../../components/DataGrid/DataGrid';
import { DataGridTemplateColumn, Props as TemplateProps } from '../../../../../components/DataGrid/DataGridTemplateColumn';
import { DataGridTextColumn } from '../../../../../components/DataGrid/DataGridTextColumn';
import { ColumnProps } from '../../../../../components/DataGrid/CellColumnProps';
import { DataGridButtonColumn } from '../../Columns/DataGridButtonColumn';
import { Button } from '../../../../../components/Buttons';
import { Sensor } from '../../../../../models/Sensor';
import { Key } from "../../../../../models";
import { IdColumn } from "../../Columns/IdColumn";
import { MultiselectColumn, multiselectPropsFrom, MultiselectProps } from "../../Columns/MultiselectColumn";
import { Polyglot } from "../../../../../polyglot";

var styles = require('./SensorGrid.scss');

interface SensorGridProps extends MultiselectProps
{
    polyglot: Polyglot;
    isAdmin: boolean;
    items?: Sensor[];

    vehicleSearchInitiator: Key;

    onClone: (datum: any, row: number) => void;
    onEdit: (datum: any, row: number) => void;
    onVehicle: (datum: any, row: number) => void;
}

const getterVehicle: Getter<Sensor> = s => s.Vehicle;
const getterGeotab: Getter<Sensor> = s => s.GeotabTitle;
const getterDisplay: Getter<Sensor> = s => s.Display;
const getterLifetime: Getter<Sensor> = s => s.Lifetime;

interface CellProps
{
    polyglot: Polyglot;
    isAdmin: boolean;
    inProgress: boolean;

    onVehicle: (datum: any, row: number) => void;
    onClone: (datum: any, row: number) => void;
}

const VehicleCell = (props: TemplateProps & CellProps) =>
{
    const { polyglot, datum, row, getValue, onClone, isAdmin, inProgress } = props;
    return (
        <td className={styles.VehicleCell}>
            <span>{getValue(datum)}</span>
            { isAdmin && <Button dim="xs" leftIcon="duplicate" type="info" onClick={e => onClone(datum, row)}>{polyglot.t('button.clone')}</Button> }
            <Button dim="xs" type="info" leftIcon="search"
                spinner={inProgress} disabled={inProgress}
                onClick={e => props.onVehicle(datum, row)}>{polyglot.t('sensor.label_vehicle')}</Button>
        </td>
    );
}

export const SensorGrid = (props: SensorGridProps) =>
{
    const { polyglot } = props;
    const withUnit = (value: number, s: Sensor) => value ? `${value} ${s.UnitName}` : value;
    const withFrac = (value: number) => value ? `${value} %`: value;

    const getterBaseline: Getter<Sensor> = s => withUnit(s.Baseline, s);
    const getterRedMax: Getter<Sensor> = s => withUnit(s.AbsLevelMaximumRed, s) || withFrac(s.RelLevelMaximumRed);
    const getterYellowMax: Getter<Sensor> = s => withUnit(s.AbsLevelMaximumYellow, s) || withFrac(s.RelLevelMaximumYellow);
    const getterYellowMin: Getter<Sensor> = s => withUnit(s.AbsLevelMinimumYellow, s) || withFrac(s.RelLevelMinimumYellow);
    const getterRedMin: Getter<Sensor> = s => withUnit(s.AbsLevelMinimumRed, s) || withFrac(s.RelLevelMinimumRed);

    return (
        <DataGrid initialData={props.items || []} className={props.className} onThead={props.onThead}>
            <MultiselectColumn {...multiselectPropsFrom(props)} />
            <IdColumn header={polyglot.t('column.id')} />
            <DataGridTemplateColumn header={polyglot.t('sensor.label_vehicle')} getValue={getterVehicle} render={(colProps: TemplateProps) => (
                <VehicleCell
                    polyglot={polyglot} isAdmin={props.isAdmin}
                    onClone={props.onClone} onVehicle={props.onVehicle}
                    inProgress={props.vehicleSearchInitiator === (colProps.datum as Sensor).id}
                    {...colProps} />
            )} />
            <DataGridTextColumn header={polyglot.t('sensor.label_native_title')} getValue={getterGeotab} />
            <DataGridTextColumn header={polyglot.t('levels.baseline')} getValue={getterBaseline} />
            <DataGridTextColumn header={polyglot.t('levels.red_max')} getValue={getterRedMax} />
            <DataGridTextColumn header={polyglot.t('levels.yellow_max')} getValue={getterYellowMax} />
            <DataGridTextColumn header={polyglot.t('levels.yellow_min')} getValue={getterYellowMin} />
            <DataGridTextColumn header={polyglot.t('levels.red_min')} getValue={getterRedMin} />
            <DataGridTextColumn header={polyglot.t('sensor.label_display')} getValue={getterDisplay} />
            <DataGridTextColumn header={polyglot.t('sensor.label_lifetime')} getValue={getterLifetime} />
            { props.isAdmin && <DataGridButtonColumn header={polyglot.t('column.action')} title={polyglot.t('button.edit_short')} onClick={props.onEdit} /> }
        </DataGrid>
    );
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/Sensor/SensorGrid/SensorGrid.tsx